import type { DetectOverflowOptions, Placement } from '@floating-ui/react-dom';
import {
  arrow,
  autoUpdate,
  detectOverflow,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import classNames from 'classnames';
import loadImage from 'image-promise';
import _ from 'lodash';
import type { DebouncedFunc } from 'lodash';
import type { HTMLAttributes } from 'react';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './HoverPreviewable.module.scss';
import { SCREEN_EDGE_PADDING } from './ContentPreviewPopOver';
import FloatingArrow from './FloatingArrow';

const DETECT_OVERFLOW_OPTIONS: Partial<DetectOverflowOptions> = {
  padding: SCREEN_EDGE_PADDING,
};

interface HoverPreviewableProps {
  id: string;
  activeId?: string | null;
  onActiveIdChange(activeId: string | null): void;
  children: (
    props: Pick<HTMLAttributes<HTMLElement>, 'onMouseEnter' | 'onMouseLeave'>,
  ) => React.ReactNode;
  placement?: Placement & `right${string}`;
  preview: React.ReactNode;
  previewWidth: number;
  previewAspectRatio?: number;
}

const HoverPreviewable = ({
  children,
  id,
  activeId,
  onActiveIdChange,
  placement = 'right-start',
  preview,
  previewWidth,
  previewAspectRatio,
}: HoverPreviewableProps) => {
  const isActive = activeId === id;
  const handleActiveChange = (active: boolean) =>
    onActiveIdChange(active ? id : null);
  const [isHidden, setIsHidden] = useState(false);
  const shouldShow = isActive && !isHidden;

  const hasBeenHoveredRef = useRef(false);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const previewWidthRef = useRef(previewWidth);
  const previewAspectRatioRef = useRef(previewAspectRatio);

  const middleware = useMemo(
    () => [
      shift(DETECT_OVERFLOW_OPTIONS),
      size({
        apply: async ({ availableWidth, availableHeight, ...state }) => {
          let width = availableWidth;
          if (previewWidthRef.current && previewWidthRef.current < width) {
            width = previewWidthRef.current;
          }
          if (previewAspectRatioRef.current) {
            const overflow = await detectOverflow(
              state,
              DETECT_OVERFLOW_OPTIONS,
            );

            const boundaryHeight =
              state.rects.floating.height - overflow.top - overflow.bottom;
            const height = width * previewAspectRatioRef.current;
            if (height > boundaryHeight) {
              width = boundaryHeight / previewAspectRatioRef.current;
            }
          }

          Object.assign(state.elements.floating.style, {
            width: `${Math.round(width)}px`,
          });
        },
        ...DETECT_OVERFLOW_OPTIONS,
      }),
      arrow({ element: arrowRef, padding: 12 }),
    ],
    [],
  );

  const {
    refs,
    floatingStyles,
    elements,
    update,
    placement: actualPlacement,
    middlewareData: { arrow: arrowData },
  } = useFloating({
    placement,
    middleware,
    whileElementsMounted: (...args) =>
      autoUpdate(...args, { ancestorScroll: false }),
  });

  useLayoutEffect(() => {
    if (
      previewWidthRef.current !== previewWidth ||
      previewAspectRatioRef.current !== previewAspectRatio
    ) {
      previewWidthRef.current = previewWidth;
      previewAspectRatioRef.current = previewAspectRatio;
      update();
    }
  }, [previewWidth, previewAspectRatio, update]);

  const documentMouseMove =
    useRef<DebouncedFunc<(event: MouseEvent) => void>>();
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>();

  const cleanup = () => {
    if (documentMouseMove.current) {
      documentMouseMove.current.cancel();
      document.removeEventListener('mousemove', documentMouseMove.current);
    }
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
  };

  const handleTargetMouseEnter = () => {
    if (isActive) {
      return;
    }
    hasBeenHoveredRef.current = true;
    if (!activeId) {
      hoverTimeout.current = setTimeout(() => {
        handleActiveChange(true);
      }, 500);
    } else {
      handleActiveChange(true);
    }
  };

  const handleTargetMouseLeave = (e: React.MouseEvent) => {
    cleanup();
    if (!isActive) {
      return;
    }
    let { clientX } = e;
    documentMouseMove.current = _.throttle(
      (event) => {
        if (!elements.floating) return;

        const { left, right, top, bottom } =
          elements.floating.getBoundingClientRect();
        const oldClientX = clientX;
        clientX = event.clientX;
        const { clientY } = event;
        const isInPopOver =
          clientX >= left &&
          clientX <= right &&
          clientY >= top &&
          clientY <= bottom;
        const oldDistance = left - oldClientX;
        const distance = left - clientX;
        const isGettingCloser = distance < oldDistance - 100;
        if (!isInPopOver && !isGettingCloser) {
          handleActiveChange(false);
        }
      },
      200,
      { leading: false },
    );
    document.addEventListener('mousemove', documentMouseMove.current);
  };

  const shouldShowRef = useRef(shouldShow);
  useLayoutEffect(() => {
    if (shouldShowRef.current !== shouldShow) {
      shouldShowRef.current = shouldShow;
      if (shouldShow) {
        update();
      }
    }
  }, [shouldShow, update]);

  useLayoutEffect(() => {
    if (!elements.floating) return undefined;

    const observer = new MutationObserver((mutations) => {
      const addedImages = Array.from(mutations).flatMap((mutation) =>
        Array.from(mutation.addedNodes).flatMap((node) =>
          node instanceof Element
            ? Array.from(node.getElementsByTagName('img'))
            : [],
        ),
      );
      if (addedImages.length === 0) return;

      setIsHidden(true);
      loadImage(addedImages)
        .catch(() => {})
        .finally(() => setIsHidden(false));
    });

    observer.observe(elements.floating, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [elements.floating]);

  useEffect(cleanup, [isActive]);

  useEffect(() => cleanup, []);

  return (
    <div
      className={classNames(styles.container, {
        [styles.show]: shouldShow,
      })}
    >
      {children({
        onMouseEnter: handleTargetMouseEnter,
        onMouseLeave: handleTargetMouseLeave,
      })}
      <div ref={refs.setReference} className={styles.reference} />
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className={styles.previewContainer}
      >
        {(hasBeenHoveredRef.current || isActive) && (
          <>
            <div className={styles.preview}>{preview}</div>
            <div className={styles.arrowContainer}>
              <FloatingArrow
                ref={arrowRef}
                placement={actualPlacement}
                arrowData={arrowData}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HoverPreviewable;
